import { DateTime } from 'luxon';

/** The slice of a production meter the forecast math needs — free of Meteor so the math runs under plain node. */
export interface ForecastModel {
  timezone?: string;
  commissioningDate?: Date;
  forecastedProduction?: number[];  // typical-year kWh, Jan..Dec
  degradationRate?: number;         // %/yr
  ppaRate?: number;                 // $/kWh
  ppaEscalator?: number;            // %/yr
}

/** Whole years from commissioning to `at` (0 before the first anniversary) — the step on which degradation and
 *  PPA escalation move, the same whole-year count productionMeterPpaRateAt uses. */
export const projectYearsAt = (commissioning: DateTime, at: DateTime) =>
  Math.max(Math.floor(at.diff(commissioning, 'years').years), 0);

/**
 * Forecast production and revenue between two instants. The window is walked in segments that each lie within
 * one calendar month (the 12-month profile) and one project year (the step at every commissioning anniversary);
 * a segment is its month's profile value prorated by time. Nothing before commissioning counts. Depends only on
 * the model and the window — never on the clock — so a pure forecast reads the same on any day.
 */
export const forecastForRange = (meter: ForecastModel, start: DateTime, end: DateTime) => {
  const { forecastedProduction } = meter;
  const deg = (meter.degradationRate || 0) / 100;
  const ppa = meter.ppaRate || 0;
  const esc = (meter.ppaEscalator || 0) / 100;
  let production = 0;
  let revenue = 0;
  if (!forecastedProduction) return { production, revenue };
  const comm = meter.commissioningDate ? DateTime.fromJSDate(meter.commissioningDate, { zone: meter.timezone }) : undefined;
  let cursor = comm && comm > start ? comm : start;
  while (cursor < end) {
    const monthStart = cursor.startOf('month');
    const monthEnd = monthStart.plus({ months: 1 });
    const years = comm ? projectYearsAt(comm, cursor) : 0;
    const stops = [monthEnd, end];
    if (comm) stops.push(comm.plus({ years: years + 1 }));
    let segmentEnd = stops.reduce((a, b) => (b < a ? b : a));
    // A Feb-29 commissioning has no exact anniversary and can put the next step on the cursor itself.
    if (segmentEnd <= cursor) segmentEnd = monthEnd < end ? monthEnd : end;
    const fraction = segmentEnd.diff(cursor).milliseconds / monthEnd.diff(monthStart).milliseconds;
    const p = fraction * (1 - years * deg) * (forecastedProduction[monthStart.month - 1] || 0);
    production += p;
    revenue += p * ppa * (1 + years * esc);
    cursor = segmentEnd;
  }
  return { production, revenue };
};
